import '../HousingRequest.scss';
import HousingRequestBanner from '../../assets/housing-request/images/housing-request-banner.jpg';
import { ErrorMessage, Field, Form, Formik, useFormikContext } from 'formik';
import { HomeRequestSchema } from '../../../utils/yupSchemas/HomeRequestSchema';
import {
  IAgencies,
  IInitRequest,
  agenciesState,
  houseReqState,
} from '../../../redux/slices/HousingRequestSlice';
import FormikFormDateTimePicker from '../../../utils/FormikFieldDateTimePicker';
import FormikFormSelectLabelValueField from '../../../utils/FormikFormSelectLabelValueField';
import FormikFormInput from '../../../utils/FormikFormInput';
import { useEffect, useRef, useState } from 'react';
import * as lodash from 'lodash';
import dayjs from 'dayjs';
import { Link } from 'react-router-dom';
import { dateFormatForBE } from '../../../utils/common';
import FormikFormPhone from '../../../utils/FormikFieldPhoneNumberInput';
import { useStateCity } from '../../../hooks/useStateCity';
import FormikFormSelectField from '../../../utils/FormikFormSelect';
import {
  BsPlusLg,
  BsSendFill,
  BsCheckCircleFill,
  BsShieldCheck,
  BsPersonBadge,
  BsHouseDoor,
  BsEnvelopePaper,
} from 'react-icons/bs';

interface IProps {
  initialValues: IInitRequest;
  submitHandler: (data: IInitRequest) => void;
  loading: boolean;
  resetRef: any;
}

const TravelReasonOptions = [
  { value: 'Corporate/Business', label: 'Corporate/Business' },
  { value: 'Student', label: 'Student' },
  { value: 'Job', label: 'Job' },
  { value: 'Traveling Healthcare', label: 'Traveling Healthcare' },
  { value: 'Allied Health', label: 'Allied Health' },
  { value: 'Physician', label: 'Physician' },
  { value: 'Transition and Location', label: 'Transition and Location' },
  { value: 'Employee Transfer', label: 'Employee Transfer' },
  { value: 'Military', label: 'Military' },
  { value: 'Oil & Gas Company', label: 'Oil & Gas Company' },
  { value: 'Insurance Realocation', label: 'Insurance Realocation' },
  { value: 'Temporary Housing', label: 'Temporary Housing' },
  { value: 'Business Traveler', label: 'Business Traveler' },
  { value: 'Other', label: 'Other' },
];

const OccupantOptions = [
  { value: '1', label: '1 Occupant' },
  { value: '2', label: '2 Occupants' },
  { value: '3', label: '3 Occupants' },
  { value: '4', label: '4 Occupants' },
  { value: '5+', label: '5+ Occupants' },
];

const Requester = {
  Tenant: 'Tenant',
  Traveler: 'Traveler',
  Agency: 'Agency',
  Military: 'Military',
  Owner: 'Owner',
};

const PropertyOptions = [
  { value: 'Any Available Property', label: 'Any Available Property' },
  { value: 'Private Unit or Room', label: 'Private Unit or Room' },
  { value: 'Hotel', label: 'Hotel' },
];

const BudgetOptions = [
  { value: '<₹1000', label: '< ₹1,000' },
  { value: '₹1000-₹3000', label: '₹1,000 - ₹3,000' },
  { value: '₹3000-₹8000', label: '₹3,000 - ₹8,000' },
  { value: '₹8000-₹12000', label: '₹8,000 - ₹12,000' },
  { value: '₹12000-₹18000', label: '₹12,000 - ₹18,000' },
  { value: '₹18000-₹25000', label: '₹18,000 - ₹25,000' },
  { value: '₹25000+', label: '₹25,000+' },
];

export const HousingRequestForm = (props: IProps) => {
  const { submitHandler, resetRef, loading, initialValues } = props;
  const [datePicker, setDatePicker] = useState(-1);
  const [setChangeHandler, setSetChangeHandler] = useState(false);
  const requesterRef = useRef<string>(Requester.Traveler);
  const stateRef = useRef<string>(initialValues.state?.value || '');
  const [state, setState] = useState('');
  const [allCity, setAllCity] = useState<any>([]);
  const [allState, setAllState] = useState<any>([]);

  const {
    states,
    stateLoading,
    cities,
    loading: cityLoading,
  } = useStateCity({
    stateCode: state,
  });

  const genderOption = [
    { label: 'Male', value: 'male' },
    { label: 'Female', value: 'female' },
  ];

  const setHandler = (i: number) => {
    setDatePicker(i);
    setSetChangeHandler(!setChangeHandler);
  };

  const FormValue = () => {
    const { values, setFieldValue, resetForm } =
      useFormikContext<IInitRequest>();

    useEffect(() => {
      if (values.requester !== requesterRef.current) {
        resetForm({
          values: {
            ...values,
            ...houseReqState,
            requester: values.requester,
          },
        });
        requesterRef.current = values.requester;
      }
    }, [values.requester]);

    useEffect(() => {
      if (values?.state && values.state.value !== stateRef.current) {
        const v: any = values?.state;
        setState(v?.value);
        setFieldValue('city', houseReqState.city);
        stateRef.current = v?.value;
      }
    }, [values?.state]);

    useEffect(() => {
      if (datePicker !== -1 && datePicker !== -99) {
        let moveInDate = new Date(values.agencies[datePicker].moveIn);
        moveInDate.setMonth(moveInDate.getMonth() + 1);
        moveInDate.setDate(moveInDate.getDate() - 1);
        setFieldValue(
          `agencies[${datePicker}].moveOut`,
          dayjs(moveInDate).format(dateFormatForBE)
        );
      } else if (datePicker === -99) {
        let moveInDate = new Date(values.traveler.moveIn);
        moveInDate.setMonth(moveInDate.getMonth() + 1);
        moveInDate.setDate(moveInDate.getDate() - 1);
        setFieldValue(
          `traveler.moveOut`,
          dayjs(moveInDate).format(dateFormatForBE)
        );
      }
    }, [datePicker, setChangeHandler]);

    return null;
  };

  const StateCityComponent = (props: { ctx: string }) => {
    return (
      <>
        <div className="col-md-6 mb-3">
          <label className="form-label font-weight-600">Preferred State</label>
          <Field
            as={FormikFormSelectField}
            id="State"
            name={`state`}
            className="form-select form-select-lg"
            options={allState}
            loading={stateLoading}
          />
        </div>

        <div className="col-md-6 mb-3">
          <label className="form-label font-weight-600">Preferred City</label>
          <Field
            as={FormikFormSelectField}
            id="City"
            name={`city`}
            className="form-select form-select-lg"
            options={allCity}
            loading={cityLoading}
          />
        </div>
      </>
    );
  };

  const TravelerComponent = (tprops: { ctx: string; index: number }) => {
    const { index: i, ctx } = tprops;
    const { values } = useFormikContext<any>();
    const selectedGender = values[ctx]?.gender;
    const selectedPet = values[ctx]?.withPet;

    const isIndividual =
      values.requester === Requester.Traveler ||
      values.requester === Requester.Tenant ||
      values.requester === Requester.Owner;

    return (
      <div id="traveler" className="traveler-form-wrapper">
        <div className="row g-3">
          {/* Section 1: Contact Information */}
          <div className="col-12">
            <div className="section-divider-title">
              <BsPersonBadge className="me-2 text-primary" />
              <span>Contact Information</span>
            </div>
          </div>

          {values.requester === Requester.Agency ? (
            <>
              <div className="col-md-6">
                <label className="form-label">First Name</label>
                <Field
                  as={FormikFormInput}
                  name={`${ctx}.firstName`}
                  className="form-control"
                  placeholder="e.g. John"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Last Name</label>
                <Field
                  as={FormikFormInput}
                  name={`${ctx}.lastName`}
                  className="form-control"
                  placeholder="e.g. Doe"
                />
              </div>
            </>
          ) : (
            <>
              <div className="col-md-6">
                <label className="form-label">First Name</label>
                <Field
                  as={FormikFormInput}
                  name={`${ctx}.firstName`}
                  className="form-control"
                  placeholder="e.g. John"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Last Name</label>
                <Field
                  as={FormikFormInput}
                  name={`${ctx}.lastName`}
                  className="form-control"
                  placeholder="e.g. Doe"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Email Address</label>
                <Field
                  as={FormikFormInput}
                  name={`${ctx}.email`}
                  className="form-control"
                  type="email"
                  placeholder="john.doe@example.com"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Phone Number</label>
                <Field
                  as={FormikFormPhone}
                  name={`${ctx}.phone`}
                  className="form-control"
                  placeholder="Mobile number"
                />
              </div>
            </>
          )}

          {/* Section 2: Requirements */}
          <div className="col-12 mt-4">
            <div className="section-divider-title">
              <BsHouseDoor className="me-2 text-primary" />
              <span>Housing Requirements</span>
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Move-In Date</label>
            <Field
              id="MoveIn"
              name={`${ctx}.moveIn`}
              setHandler={() => setHandler(i)}
              type="MoveIn"
              autoComplete="MoveIn"
              required
              className="form-control"
              outFormat={dateFormatForBE}
              inputFormat={dateFormatForBE}
              disablePast
              min={new Date().setDate(new Date().getDate() + 1)}
              as={FormikFormDateTimePicker}
            />
            <ErrorMessage
              name={`${ctx}.moveIn`}
              component="div"
              className="text-danger small mt-1"
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Reason For Travel</label>
            <Field
              as={FormikFormSelectLabelValueField}
              isLabelValueSame={true}
              id="TravelReason"
              name={`${ctx}.travelReason`}
              className="form-select"
              options={TravelReasonOptions}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Your Occupation</label>
            <Field
              as={FormikFormInput}
              name={`${ctx}.working`}
              className="form-control"
              type="text"
              placeholder="e.g. Software Engineer, Doctor, Student"
            />
          </div>

          {isIndividual && <StateCityComponent ctx="" />}

          <div className="col-md-6">
            <label className="form-label">Total Occupants</label>
            <Field
              as={FormikFormSelectLabelValueField}
              isLabelValueSame={true}
              id="Occupant"
              name={`${ctx}.occupant`}
              className="form-select"
              options={OccupantOptions}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Property Preference</label>
            <Field
              as={FormikFormSelectLabelValueField}
              isLabelValueSame={true}
              id="PropertyType"
              name={`${ctx}.propertyType`}
              className="form-select"
              options={PropertyOptions}
            />
          </div>

          <div className="col-md-6">
            <label className="form-label">Monthly Budget</label>
            <Field
              as={FormikFormSelectLabelValueField}
              isLabelValueSame={true}
              id="Budget"
              name={`${ctx}.budget`}
              className="form-select"
              options={BudgetOptions}
            />
          </div>

          {/* Gender Chip Selection */}
          <div className="col-md-6">
            <label className="form-label d-block">Gender</label>
            <div className="custom-pill-group">
              {genderOption.map((option) => (
                <label
                  key={option.value}
                  className={`pill-option ${selectedGender === option.value ? 'active' : ''}`}
                >
                  <Field
                    type="radio"
                    name={`${ctx}.gender`}
                    value={option.value}
                    className="d-none"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>
            <ErrorMessage
              name={`${ctx}.gender`}
              component="div"
              className="text-danger small mt-1"
            />
          </div>

          {/* Pet Chip Selection */}
          <div className="col-md-6">
            <label className="form-label d-block">Are pets coming along?</label>
            <div className="custom-pill-group">
              {['No', 'Yes'].map((petOpt) => (
                <label
                  key={petOpt}
                  className={`pill-option ${selectedPet === petOpt ? 'active' : ''}`}
                >
                  <Field
                    type="radio"
                    name={`${ctx}.withPet`}
                    value={petOpt}
                    className="d-none"
                  />
                  <span>{petOpt}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Section 3: Direct Contact Preference */}
          {isIndividual && (
            <>
              <div className="col-12 mt-4">
                <div className="section-divider-title">
                  <BsEnvelopePaper className="me-2 text-primary" />
                  <span>Communication Preferences</span>
                </div>
              </div>

              <div className="col-12">
                <label className="form-label">
                  How should property owners reach out?
                </label>
                <div className="row g-3">
                  <div className="col-sm-6">
                    <label
                      className={`contact-preference-card ${values[ctx]?.contacted === 'Phone' ? 'active' : ''}`}
                    >
                      <Field
                        type="radio"
                        name={`${ctx}.contacted`}
                        value="Phone"
                        className="form-check-input me-2"
                      />
                      <span>Phone Call / WhatsApp</span>
                    </label>
                  </div>
                  <div className="col-sm-6">
                    <label
                      className={`contact-preference-card ${values[ctx]?.contacted === 'Email' ? 'active' : ''}`}
                    >
                      <Field
                        type="radio"
                        name={`${ctx}.contacted`}
                        value="Email"
                        className="form-check-input me-2"
                      />
                      <span>Email Message</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="col-12 mt-3">
                <label className="form-label">
                  Additional Message or Specific Preferences
                </label>
                <Field
                  component="textarea"
                  rows={3}
                  name={`${ctx}.description`}
                  className="form-control"
                  placeholder="e.g. Looking for a fully furnished room near tech park, non-smoker, quiet neighborhood preferred..."
                />
                <ErrorMessage
                  className="text-danger small mt-1"
                  component="div"
                  name={`${ctx}.description`}
                />
              </div>

              <div className="col-12 mt-3">
                <div className="form-check custom-checkbox">
                  <Field
                    type="checkbox"
                    name="termCondition"
                    id="termCondition"
                    className="form-check-input"
                  />
                  <label
                    htmlFor="termCondition"
                    className="form-check-label text-muted small ms-1"
                  >
                    I have read and agree to the{' '}
                    <Link
                      to="/terms-and-conditions"
                      className="text-primary text-decoration-underline"
                    >
                      Terms of Use & Privacy Policy
                    </Link>
                    .
                  </label>
                </div>
              </div>

              <div className="col-12 text-center mt-4">
                <button
                  disabled={!values.termCondition || loading}
                  type="submit"
                  className="btn btn-primary btn-lg rounded-pill px-5 shadow-sm btn-submit-custom"
                >
                  {loading ? (
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                  ) : (
                    <BsSendFill className="me-2" />
                  )}
                  Submit Inquiry
                </button>
                <p className="text-muted small mt-2 mb-0">
                  <BsShieldCheck className="text-success me-1" />
                  Your information is shared only with verified owners matching
                  your criteria.
                </p>
              </div>
            </>
          )}

          {values.requester === Requester.Agency && (
            <div className="col-12 mt-3">
              <label className="form-label">Traveler Notes</label>
              <Field
                component="textarea"
                rows={3}
                name={`${ctx}.description`}
                className="form-control"
                placeholder="Notes regarding this specific traveler..."
              />
              <ErrorMessage
                className="text-danger small mt-1"
                component="div"
                name={`${ctx}.description`}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (states?.length && !lodash.isEqual(states, allState)) {
      const stateOptions = states.map((e) => ({
        value: e.code,
        label: e.name,
      }));

      if (!lodash.isEqual(stateOptions, allState)) {
        setAllState(stateOptions);
      }
    }
  }, [states]);

  useEffect(() => {
    if (cities.length) {
      const city = cities?.map((e, i) => {
        return { value: `${e.code}_${i}`, label: e.name };
      });
      setAllCity(city);
    }
  }, [cities]);

  return (
    <div className="housing-request">
      {/* Enhanced Hero Banner */}
      <div
        className="inner-banner"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(15, 23, 42, 0.85) 0%, rgba(30, 58, 138, 0.75) 100%)`,
        }}
      >
        <div className="container py-5">
          <div className="row justify-content-center text-center">
            <div className="col-lg-9 col-xl-8">
              <span className="badge bg-primary-soft text-white rounded-pill px-3 mb-3 fw-semibold">
                ⚡ Fast & Free Housing Matching
              </span>
              <h1 className="banner-heading text-white fw-bold mb-3">
                Find Your Ideal Accommodation Without Browsing Endless Listings
              </h1>
              <p className="banner-subtext text-white-50 lead fs-6">
                In a hurry? Simply submit a housing inquiry. Verified property
                owners in your target area who match your criteria will reach
                out to you directly.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 3-Step Process Highlights */}
      <section className="request-steps-section py-4 bg-white border-bottom">
        <div className="container">
          <div className="row g-3">
            <div className="col-md-4">
              <div className="step-card">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h6 className="fw-bold mb-1">Fill Inquiry Form</h6>
                  <p className="text-muted small mb-0">
                    Tell us your budget, preferred location, and stay
                    preferences.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="step-card">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h6 className="fw-bold mb-1">Instant Owner Notification</h6>
                  <p className="text-muted small mb-0">
                    We match your criteria with verified property owners nearby.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="step-card">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h6 className="fw-bold mb-1">Receive Direct Offers</h6>
                  <p className="text-muted small mb-0">
                    Owners contact you directly with tailored available units.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Form Section */}
      <section id="housing-request-form" className="py-5 position-relative overflow-hidden form-bg-grid">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 col-xl-9">
              <div className="card shadow-lg border-0 rounded-4 overflow-hidden">
                <div className="card-header bg-white border-0 pt-4 px-4 px-md-5 text-center">
                  <h3 className="fw-bold text-dark mb-1">
                    Housing Request Form
                  </h3>
                  <p className="text-muted small">
                    Complete the details below to broadcast your request to
                    trusted hosts
                  </p>
                </div>

                <div className="card-body p-4 p-md-5 bg-white">
                  <Formik
                    initialValues={initialValues}
                    enableReinitialize
                    validationSchema={HomeRequestSchema}
                    onSubmit={(values: any) => submitHandler(values)}
                  >
                    {({ values, setFieldValue, resetForm }) => (
                      <Form className="needs-validation">
                        {/* Dynamic Context Renderers */}
                        {values?.requester === Requester.Traveler && (
                          <TravelerComponent index={-99} ctx="traveler" />
                        )}
                        {values?.requester === Requester.Tenant && (
                          <TravelerComponent index={-99} ctx="tenant" />
                        )}
                        {values?.requester === Requester.Owner && (
                          <TravelerComponent index={-99} ctx="owner" />
                        )}

                        <FormValue />

                        {/* Agency Specific Section */}
                        {values?.requester === Requester.Agency && (
                          <div id="agency" className="mt-2">
                            <div className="row">
                              <StateCityComponent ctx="" />
                            </div>

                            {/* Dynamic Traveler Cards */}
                            {values?.agencies?.map(
                              (e: IAgencies, idx: number) => (
                                <div
                                  key={idx}
                                  className="traveler-card card border-0 bg-light rounded-3 p-3 p-md-4 mb-4 mt-3"
                                >
                                  <div className="d-flex align-items-center justify-content-between mb-3 border-bottom pb-2">
                                    <h5 className="mb-0 text-primary fw-semibold fs-6">
                                      Traveler #{idx + 1}
                                    </h5>
                                  </div>
                                  <TravelerComponent
                                    index={idx}
                                    ctx={`agencies[${idx}]`}
                                  />
                                </div>
                              )
                            )}

                            {/* Agency Details Fields */}
                            <div className="agency-details border-top pt-4 mt-4">
                              <h5 className="fs-6 fw-bold mb-3 text-dark">
                                Agency Information
                              </h5>
                              <div className="row g-3">
                                <div className="col-md-6 col-lg-4">
                                  <label className="form-label">
                                    Agency Name
                                  </label>
                                  <Field
                                    as={FormikFormInput}
                                    className="form-control"
                                    id="agencyName"
                                    name="agency.name"
                                    placeholder="Company name"
                                  />
                                </div>

                                <div className="col-md-6 col-lg-4">
                                  <label className="form-label">
                                    Agency Contact
                                  </label>
                                  <Field
                                    as={FormikFormInput}
                                    className="form-control"
                                    id="agencyContact"
                                    name="agency.contact"
                                    placeholder="Phone number"
                                  />
                                </div>

                                <div className="col-md-12 col-lg-4">
                                  <label className="form-label">
                                    Agency Email
                                  </label>
                                  <Field
                                    as={FormikFormInput}
                                    className="form-control"
                                    id="agencyEmail"
                                    name="agency.email"
                                    type="email"
                                    placeholder="agency@domain.com"
                                  />
                                </div>
                              </div>
                            </div>

                            {/* Terms & Action Buttons Bar */}
                            <div className="row align-items-center g-3 mt-4 border-top pt-4">
                              <div className="col-md-6">
                                <div className="form-check custom-checkbox">
                                  <Field
                                    type="checkbox"
                                    name="termCondition"
                                    id="termConditionAgency"
                                    className="form-check-input"
                                  />
                                  <label
                                    htmlFor="termConditionAgency"
                                    className="form-check-label text-muted small ms-1"
                                  >
                                    I have read and agree to the{' '}
                                    <Link
                                      to="/terms-and-conditions"
                                      className="text-decoration-underline text-primary"
                                    >
                                      Terms of Use
                                    </Link>
                                    .
                                  </label>
                                </div>
                              </div>

                              <div className="col-md-6">
                                <div className="d-flex flex-wrap justify-content-md-end align-items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const newValue = lodash.cloneDeep(
                                        values.agencies || []
                                      );
                                      newValue.push(agenciesState);
                                      setFieldValue('agencies', newValue);
                                    }}
                                    className="btn btn-outline-primary rounded-pill px-3 py-2 d-inline-flex align-items-center gap-2"
                                  >
                                    <BsPlusLg />
                                    <span>Add Traveler</span>
                                  </button>

                                  <button
                                    disabled={!values.termCondition || loading}
                                    type="submit"
                                    className="btn btn-primary rounded-pill px-4 py-2 d-inline-flex align-items-center gap-2 shadow-sm"
                                  >
                                    {loading ? (
                                      <span
                                        className="spinner-border spinner-border-sm"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                    ) : (
                                      <BsSendFill />
                                    )}
                                    <span>Submit Request</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Hidden Reset Trigger */}
                        <button
                          type="button"
                          className="d-none"
                          onClick={() => resetForm()}
                          ref={resetRef}
                        >
                          Reset
                        </button>
                      </Form>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
